/**
 * @description Hooks for agent instance operations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createAgentInstance, installAgent, updateAgentInstance, BASE_URL } from '@/services/DataService';
import { logInfo, logger } from '@/lib/logger';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';

interface CreateAgentInstancePayload {
  agent_instance: {
    agent_id: string;
    config?: {
      [key: string]: unknown;
    };
  };
}

interface UpdateAgentInstancePayload {
  agent_instance: {
    config: {
      [key: string]: unknown;
    };
  };
}

export const useCreateAgentInstance = () => {
  const queryClient = useQueryClient();
  const trackEvent = useTrack();

  return useMutation({
    mutationFn: (payload: CreateAgentInstancePayload) => {
      if (!BASE_URL) {
        throw new Error('Base URL is not configured');
      }
      return createAgentInstance(payload);
    },
    onSuccess: (data, payload) => {
      logInfo('Agent instance created successfully', { data });
      trackEvent(ANALYTICS_EVENTS.AGENT_INSTALLED, {
        agent_slug: payload.agent_instance.agent_id,
      });
      // Invalidate and refetch agent instances list
      queryClient.invalidateQueries({ queryKey: ['agent-instances'] });
    },
    onError: (error) => {
      logger(error as Error, { context: 'useCreateAgentInstance: Error creating agent instance' });
    },
  });
};

export const useInstallAgent = () => {
  const queryClient = useQueryClient();
  const trackEvent = useTrack();

  return useMutation({
    mutationFn: (agentShortName: string) => {
      if (!BASE_URL) {
        throw new Error('Base URL is not configured');
      }
      return installAgent(agentShortName);
    },
    onSuccess: (data, agentShortName) => {
      logInfo('Agent installed successfully', { data });
      trackEvent(ANALYTICS_EVENTS.AGENT_INSTALLED, { agent_slug: agentShortName });
      queryClient.invalidateQueries({ queryKey: ['agent-instances'] });
    },
    onError: (error) => {
      logger(error as Error, { context: 'useInstallAgent: Error installing agent' });
    },
  });
};

export const useUpdateAgentInstance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentInstanceId, payload }: { agentInstanceId: string; payload: UpdateAgentInstancePayload }) => {
      if (!BASE_URL) {
        throw new Error('Base URL is not configured');
      }
      return updateAgentInstance(agentInstanceId, payload);
    },
    onSuccess: (data) => {
      logInfo('Agent instance updated successfully', { data });
      // Invalidate and refetch agent instances list
      queryClient.invalidateQueries({ queryKey: ['agent-instances'] });
    },
    onError: (error) => {
      logger(error as Error, { context: 'useUpdateAgentInstance: Error updating agent instance' });
    },
  });
};